/**
 * Canonical analytics event names (#100).
 *
 * Single source of truth for analytics event naming across Mixpanel, the
 * backend funnel counters, and (eventually) GA4. All names are snake_case and
 * mirror `backend/metrics/events.py::FUNNEL_CONTRACT` so the two sides can
 * never drift.
 *
 * Monetization funnel (owner→purchase):
 *   funnel_entry_viewed → signup_started → signup_completed → role_selected
 *   → onboarding_started → onboarding_completed → plan_selected → pricing_viewed
 *   → checkout_started → payment_submitted → payment_failed
 *   → purchase_completed → activation_completed
 *
 * Legacy aliases (registration_started, sign_up, licenses_chosen, …) are kept
 * only as string constants for backward-safe call-site migration; new code
 * must use the canonical names below.
 */
export const AnalyticsEvent = {
  FunnelEntryViewed: 'funnel_entry_viewed',
  SignupStarted: 'signup_started',
  SignupCompleted: 'signup_completed',
  RoleSelected: 'role_selected',
  OnboardingStarted: 'onboarding_started',
  OnboardingCompleted: 'onboarding_completed',
  PlanSelected: 'plan_selected',
  PricingViewed: 'pricing_viewed',
  CheckoutStarted: 'checkout_started',
  PaymentSubmitted: 'payment_submitted',
  PaymentFailed: 'payment_failed',
  PurchaseCompleted: 'purchase_completed',
  ActivationCompleted: 'activation_completed',
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

/**
 * Legacy event-name constants. DO NOT use in new code — migrate call sites to
 * the canonical `AnalyticsEvent` names. Listed here so old references still
 * compile during the transition window.
 */
export const LegacyAnalyticsEvent = {
  RegistrationStarted: 'registration_started',
  SignUp: 'sign_up',
  LicensesChosen: 'licenses_chosen',
  FleetSizeDeclared: 'fleet_size_declared',
  ProductChosen: 'product_chosen',
  BillingCycleChosen: 'billing_cycle_chosen',
  LoginSuccess: 'login_success',
} as const;
